import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { OrganizationService } from '../services/organization.service';

@Component({
  selector: 'app-org-receipt-view',
  templateUrl: './org-receipt-view.component.html',
  styleUrls: ['./org-receipt-view.component.scss']
})
export class OrgReceiptViewComponent implements OnInit {
  imageUrl: SafeUrl | null;
  expenseId: string | null;
  organization: any;
  expense: any;
  expenseDate: Date | null = null;
  categories = [
    'ADVERTISING',
    'CAR_AND_TRUCK_EXPENSES',
    'COMMISSIONS_AND_FEES',
    'CONTRACT_LABOR',
    'DEPRECIATION',
    'EMPLOYEE_WAGES',
    'INSURANCE_OTHER_THAN_HEALTH',
    'INTEREST_MORTGAGE_AND_OTHER',
    'LEGAL_AND_PROFESSIONAL_SERVICES',
    'OFFICE_EXPENSES',
    'RENT_OR_LEASE',
    'REPAIRS_AND_MAINTENANCE',
    'SUPPLIES',
    'TAXES_AND_LICENSES',
    'TRAVEL',
    'MEALS',
    'UTILITIES',
    'OTHER_EXPENSES'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private organizationService: OrganizationService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.expenseId = this.route.snapshot.queryParamMap.get('expenseId');
    if (this.expenseId) {
      // Load expense directly by ID (works after upload)
      this.organizationService.getExpenseById(this.expenseId).then((expense: any) => {
        this.expense = expense;
        if (this.expense) {
          // Handle both receiptUrl and receipt_url
          const receiptUrl = (this.expense as any).receiptUrl || (this.expense as any).receipt_url;
          if (receiptUrl) {
            this.loadImage(receiptUrl);
          }
          // Initialize date from expense
          if (this.expense.date) {
            this.expenseDate = new Date(this.expense.date);
          } else {
            this.expenseDate = new Date();
          }
        }
        // Also load organization
        this.organizationService.getOrganization().then(org => {
          this.organization = org;
        });
      }).catch((error) => {
        console.error('Error loading expense:', error);
        // Fallback to old method if direct load fails
        this.organizationService.getOrganization().then(org => {
          this.organization = org;
          if (this.organization && this.organization.expenses) {
            this.expense = this.organization.expenses.find((e: any) => e.id == this.expenseId);
            if (this.expense) {
              const receiptUrl = (this.expense as any).receiptUrl || (this.expense as any).receipt_url;
              this.loadImage(receiptUrl);
              // Initialize date from expense
              if (this.expense.date) {
                this.expenseDate = new Date(this.expense.date);
              } else {
                this.expenseDate = new Date();
              }
            }
          }
        });
      });
    } else {
      // Load organization even if no expenseId
      this.organizationService.getOrganization().then(org => {
        this.organization = org;
      });
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && this.expense && this.expense.id) {
      // Upload new receipt image
      this.organizationService.updateExpenseReceipt(this.expense.id, file).then((updatedExpense: any) => {
        const url = updatedExpense && updatedExpense.receiptUrl;
        (this.expense as any).receiptUrl = url;
        this.loadImage(url);
      }).catch((error) => {
        console.error('Error updating receipt image:', error);
        alert('Failed to update receipt image. Please try again.');
      });
    }
    // Clear input so same file can be uploaded again if needed
    event.target.value = '';
  }

  onDateChange(event: any) {
    if (event.value && this.expense) {
      // Update expense date when date picker changes
      this.expense.date = event.value.toISOString();
    }
  }

  save() {
    if (this.expense && this.expense.id) {
      // Ensure date is set from expenseDate if it exists
      if (this.expenseDate) {
        this.expense.date = this.expenseDate.toISOString();
      }
      // Update the expense via the API
      this.organizationService.updateExpense(this.expense).then(() => {
        // Clear organization cache to force refresh
        this.organizationService.getOrganization().then(() => {
          // Navigate back to expenses page
          this.router.navigate(['organization-expenses']);
        }).catch(() => {
          // Navigate back even if refresh fails
          this.router.navigate(['organization-expenses']);
        });
      }).catch((error) => {
        console.error('Error saving expense:', error);
        // Still navigate back even if there's an error
        this.router.navigate(['organization-expenses']);
      });
    }
  }

  close() {
    this.router.navigate(['organization-expenses']);
  }

  private loadImage(path: string) {
    if (!path) {
      this.imageUrl = null;
      return;
    }

    // Data URLs can be shown directly.
    if (path.startsWith('data:')) {
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(path);
      return;
    }

    // Receipts are private in S3, so never load a raw S3 URL in the browser:
    // extract the filename and go through the authenticated receipts endpoint.
    if (path.includes('amazonaws.com')) {
      // Extract filename from S3 URL
      let fileName = path;
      if (path.includes('/')) {
        fileName = path.substring(path.lastIndexOf('/') + 1);
        // If path contains expense-receipts/, extract just the filename
        if (path.includes('expense-receipts/')) {
          const parts = path.split('expense-receipts/');
          if (parts.length > 1) {
            fileName = parts[1].split('?')[0]; // Remove query params if any
          }
        }
      }
      path = `/v1/admin/organization/receipts/${fileName}`;
    } else if (path.startsWith('http')) {
      // Some other absolute URL (not S3): use it directly.
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(path);
      return;
    }

    // For relative paths, fetch via API
    this.organizationService.getReceiptImage(path).then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
    }).catch((error) => {
      console.error('Error loading receipt image:', error);
      this.imageUrl = null;
    });
  }
}
